'use client';

import { useCallback } from 'react';

export interface WorkspaceNavigateOptions {
  /** Replace the current history entry instead of pushing a new one. */
  replace?: boolean;
  /** Skip the scroll-to-top a real navigation performs. */
  preserveScroll?: boolean;
}

/**
 * Client-side navigation within the workspace shell (spec:
 * workspace-static-spa-routing).
 *
 * Intra-workspace navigation must NOT go through `next/router` — under static
 * export Next would try to fetch a per-route RSC payload for the concrete
 * tenant path, which is never generated, causing a reload loop. Instead we
 * change the URL with the History API; `useWorkspacePath` is subscribed to
 * `pushState`/`replaceState` (patched there) and re-renders the shell, which
 * re-resolves the view from the registry. No server round-trip occurs.
 *
 * Use this only for targets that stay within the workspace shell (including
 * cross-tenant switches). To LEAVE `/workspace` (e.g. `/main`, `/login`), use
 * normal `next/navigation` so Next handles the real route.
 */
export function useWorkspaceNavigate(): (
  href: string,
  options?: WorkspaceNavigateOptions,
) => void {
  return useCallback((href: string, options: WorkspaceNavigateOptions = {}) => {
    if (typeof window === 'undefined') return;
    const { replace = false, preserveScroll = false } = options;
    if (replace) {
      window.history.replaceState(window.history.state, '', href);
    } else {
      window.history.pushState(window.history.state, '', href);
    }
    if (!preserveScroll) window.scrollTo(0, 0);
  }, []);
}
